import { useMemo, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/utils/cn'
import {
  SERIES_COLORS,
  buildWeekGrid,
  colorIndex,
  weeksFromLessons,
  type RankEntry,
  type WeekGrid,
  type WeekPlanBlock,
  type WeekRankMode,
} from '@/utils/brockenWeekPlan'
import type { BrockenLesson } from '@/utils/brockenUnits'

function initials(name: string): string {
  return name.split(/[, ]/).filter(Boolean).map(s => s[0]).join('').slice(0, 2).toUpperCase()
}

function RankPanel({ entries, title, sub }: { entries: RankEntry[]; title: string; sub: string }) {
  return (
    <div>
      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{sub}</div>
      {entries.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Keine Priorität gesetzt — Rangliste nach Affinität. Der Tag bleibt in der Anfrage.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {entries.map(e => (
            <span key={e.teacherId} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-1 pr-2 py-0.5 text-[11.5px]">
              <span className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 text-[9px] font-bold grid place-items-center text-gray-700 dark:text-gray-200">
                {initials(e.teacherName)}
              </span>
              {e.teacherName}
              <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-full px-1.5">#{e.rank}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function WeekGridView({
  grid,
  active,
  onPick,
  compact,
}: {
  grid: WeekGrid
  active: string | null
  onPick: (id: string, entries: RankEntry[], label: string, date: string) => void
  compact?: boolean
}) {
  const colorKeys = grid.days.flatMap(d => d.segs.filter(s => !s.empty).map(s => s.colorKey))
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-2" data-week-monday={grid.monday}>
      <div className="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-2">
        Woche {grid.label}
      </div>
      <div
        className="grid gap-1"
        style={{ gridTemplateColumns: `18px repeat(${grid.days.length}, minmax(0, 1fr))`, minHeight: compact ? 96 : 200 }}
      >
        <div className="flex flex-col gap-0.5">
          <span className={cn('block', compact ? 'h-7' : 'h-8')} />
          {grid.slots.map((s, i) => (
            <span key={s.id} className="flex-1 grid place-items-center text-[10px] font-bold text-gray-400">{i + 1}</span>
          ))}
        </div>
        {grid.days.map(day => (
          <div key={day.date} className="flex flex-col gap-0.5 min-w-0">
            <div className={cn('text-center leading-tight', compact ? 'h-7' : 'h-8')}>
              <div className="text-[10px] font-bold text-gray-700 dark:text-gray-200">{day.heading}</div>
              <div className="text-[10px] text-gray-400">{day.sub}</div>
            </div>
            {day.off ? (
              grid.slots.map(s => (
                <div key={s.id} className="flex-1 rounded-md bg-gray-100 dark:bg-gray-800" />
              ))
            ) : (
              day.segs.map((seg, i) => {
                if (seg.empty) {
                  return <div key={`${day.date}-e-${i}`} className="flex-1 rounded-md bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-800" />
                }
                const pal = SERIES_COLORS[colorIndex(seg.colorKey, colorKeys)]!
                const on = active === seg.seriesId
                const dim = !!active && !on
                return (
                  <button
                    key={`${day.date}-${seg.seriesId}-${i}`}
                    type="button"
                    data-series={seg.seriesId}
                    data-date={seg.date}
                    style={{ flex: seg.span }}
                    onClick={() => onPick(seg.seriesId, seg.entries, seg.label, seg.date)}
                    className={cn(
                      'rounded-md border px-1 py-0.5 text-[10px] font-bold leading-tight text-left min-h-[28px]',
                      pal.bg, pal.text, pal.border,
                      on && 'outline outline-2 outline-gray-900 dark:outline-gray-100',
                      dim && 'opacity-40',
                    )}
                  >
                    {seg.label}
                  </button>
                )
              })
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export function BrockenWeekPlan({
  lessons,
  blocks,
  mode,
  onModeChange,
  keepTogether = true,
}: {
  lessons: BrockenLesson[]
  blocks: WeekPlanBlock[]
  mode: WeekRankMode
  onModeChange: (mode: WeekRankMode) => void
  keepTogether?: boolean
}) {
  const weeks = useMemo(() => weeksFromLessons(lessons), [lessons])
  const [openExtra, setOpenExtra] = useState<string | null>(null)
  const [active, setActive] = useState<{ id: string; entries: RankEntry[]; label: string; date: string } | null>(null)

  const first = weeks[0]
  const extra = weeks.slice(1)
  const showMode = extra.length > 0

  const firstGrid = useMemo(
    () => first ? buildWeekGrid(lessons, blocks, first, mode, keepTogether) : null,
    [lessons, blocks, first, mode, keepTogether],
  )
  const extraGrids = useMemo(
    () => extra.map(w => buildWeekGrid(lessons, blocks, w, mode, keepTogether)),
    [lessons, blocks, extra, mode, keepTogether],
  )

  const pick = (id: string, entries: RankEntry[], label: string, date: string) => {
    setActive(cur => cur?.id === id ? null : { id, entries, label, date })
  }

  if (!firstGrid) return null

  const extraOpen = (monday: string) => (openExtra ?? (mode === 'each' ? extra[0]?.monday : null)) === monday

  return (
    <div data-week-plan>
      {showMode && (
        <div className="grid grid-cols-2 gap-2 mb-3" role="radiogroup" aria-label="Wochen-Prioritäten">
          <button
            type="button"
            data-mode="repeat"
            aria-pressed={mode === 'repeat'}
            onClick={() => { onModeChange('repeat'); setActive(null) }}
            className={cn(
              'text-left rounded-xl border p-3',
              mode === 'repeat' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700',
            )}
          >
            <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
              Wochenweise wiederholen
              <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-blue-600 bg-blue-100 dark:bg-blue-900/40 px-1.5 py-0.5 rounded-full">Default</span>
            </span>
            <span className="block text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-snug">
              Freitag-Ränge gelten für alle Freitage. Extra-Wochen nur zum Nachlesen.
            </span>
          </button>
          <button
            type="button"
            data-mode="each"
            aria-pressed={mode === 'each'}
            onClick={() => { onModeChange('each'); setActive(null); setOpenExtra(extra[0]?.monday ?? null) }}
            className={cn(
              'text-left rounded-xl border p-3',
              mode === 'each' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700',
            )}
          >
            <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">Jede Woche einzeln</span>
            <span className="block text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-snug">
              Jede Woche eigene Ränge. Wochen sind Akkordeons.
            </span>
          </button>
        </div>
      )}

      <WeekGridView grid={firstGrid} active={active?.id ?? null} onPick={pick} />

      {extraGrids.map(g => {
        const open = extraOpen(g.monday)
        return (
          <div
            key={g.monday}
            className={cn('border border-gray-200 dark:border-gray-700 rounded-xl mt-2 overflow-hidden', open && 'border-indigo-200 dark:border-indigo-800')}
            data-week-acc={g.monday}
          >
            <button
              type="button"
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
              aria-expanded={open}
              onClick={() => setOpenExtra(open ? null : g.monday)}
            >
              <ChevronDown className={cn('w-4 h-4 text-gray-400 transition-transform', open && 'rotate-180')} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">Woche {g.label}</span>
                <span className="block text-[11px] text-gray-500">
                  {mode === 'repeat' ? 'gleiche Prioritäten' : 'eigene Ränge'}
                </span>
              </span>
            </button>
            {open && (
              <div className="px-2 pb-3">
                <WeekGridView grid={g} active={active?.id ?? null} onPick={pick} compact />
                <p className={cn(
                  'mt-2 text-[11.5px] leading-snug rounded-lg px-2.5 py-2',
                  mode === 'repeat'
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200'
                    : 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200',
                )}>
                  {mode === 'repeat'
                    ? 'Ränge kommen aus der Musterwoche. Gleiche Farbe = dieselbe Serie.'
                    : 'Diese Woche zählt getrennt. Gleiche Farbe, eigene Ränge.'}
                </p>
              </div>
            )}
          </div>
        )
      })}

      <div className={cn(
        'mt-3 rounded-xl border px-3 py-2.5 min-h-[76px]',
        active ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50' : 'border-dashed border-gray-300 dark:border-gray-600',
      )}>
        {active ? (
          <RankPanel
            entries={active.entries}
            title={active.label}
            sub={mode === 'repeat' ? 'Ränge gelten für alle gleichen Wochentage' : active.date}
          />
        ) : (
          <p className="text-[12.5px] text-gray-400">Klick auf einen Brocken zeigt die Ränge.</p>
        )}
      </div>
    </div>
  )
}
